"use client";

import React from "react";
import { Spinner } from "@heroui/react";
// @ts-ignore - pptx-preview doesn't have TypeScript definitions
import { init } from "pptx-preview";

import { findTextMatch, normalizeText, MatchResult } from "@/utils/textMatching";
import { logger } from "@/telemetry";

export interface PPTXViewerProps {
  /** PPTX document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Text to find and highlight in the PPTX */
  citationText?: string;

  /** Callback when text is found or not found */
  onMatchResult?: (result: MatchResult | null) => void;
}

/**
 * PPTX Viewer component using pptx-preview
 * Renders PPTX and highlights citation text
 *
 * LESSONS LEARNED FROM DOCX:
 * 1. Always render container div (not conditional)
 * 2. Use CSS display:none for loading states
 * 3. Keep direct reference to first highlight mark
 * 4. Use setTimeout for scrolling
 * 5. Character-to-node mapping for precise highlighting
 */
export const PPTXViewer: React.FC<PPTXViewerProps> = ({
  documentData,
  citationText,
  onMatchResult,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [documentText, setDocumentText] = React.useState<string>("");
  const previewerRef = React.useRef<any>(null);

  // Initialize PPTX previewer and render document
  React.useEffect(() => {
    if (!documentData || !containerRef.current) {
      return;
    }

    let isMounted = true;

    const renderPPTX = async () => {
      const container = containerRef.current;

      if (!container) return;

      try {
        setLoading(true);
        setError(null);

        // Clear container
        container.innerHTML = "";

        // Initialize pptx-preview
        if (!previewerRef.current) {
          previewerRef.current = init(container, {
            width: container.clientWidth || 960,
            height: 600,
          });
        }

        // Render PPTX
        await previewerRef.current.preview(documentData);

        if (!isMounted) return;

        // Extract text content from rendered HTML
        const extractedText = container.innerText || container.textContent || "";

        setDocumentText(extractedText);
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to render PPTX", err);
        if (isMounted) {
          setError("Failed to render PPTX document");
          setLoading(false);
        }
      }
    };

    renderPPTX();

    return () => {
      isMounted = false;
    };
  }, [documentData]);

  // Find and highlight citation text
  React.useEffect(() => {
    if (!citationText || !documentText || !containerRef.current) return;

    const matchResult = findTextMatch(documentText, citationText);

    if (onMatchResult) {
      onMatchResult(matchResult);
    }

    if (matchResult) {
      highlightTextInDOM(containerRef.current, citationText, matchResult);
    }
  }, [citationText, documentText, onMatchResult]);

  /**
   * Highlight matched text in the rendered PPTX DOM
   * Uses character-to-node mapping for precise highlighting (lesson from DOCX)
   */
  const highlightTextInDOM = (
    container: HTMLElement,
    citationText: string,
    _matchResult: MatchResult,
  ) => {
    try {
      // Get all text nodes in the document
      // eslint-disable-next-line no-undef
      const textNodes: Text[] = [];

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);

      let node;

      while ((node = walker.nextNode())) {
        if (node.nodeValue && node.nodeValue.trim().length > 0) {
          // eslint-disable-next-line no-undef
          textNodes.push(node as Text);
        }
      }

      if (textNodes.length === 0) {
        logger.info("⚠️ No text nodes found in PPTX container (slides may still be rendering)");

        return;
      }

      // Build normalized text with character-to-node mapping (same approach as PDF/DOCX)
      const isWordChar = (ch: string) => /[\p{L}\p{N}]/u.test(ch);
      const charToNodeIndex: number[] = [];
      let fullText = "";

      textNodes.forEach((node, idx) => {
        const curr = normalizeText(node.nodeValue || "");
        const prev = idx > 0 ? normalizeText(textNodes[idx - 1].nodeValue || "") : "";

        // Smart spacing: insert space only if needed
        const prevLast = prev.slice(-1);
        const currFirst = curr.slice(0, 1);
        const shouldInsertSpace =
          idx > 0 && isWordChar(prevLast) && isWordChar(currFirst) && prevLast !== "-";

        if (shouldInsertSpace) {
          fullText += " ";
          charToNodeIndex.push(-1); // separator, belongs to no node
        }

        // Append current normalized text and map each character to this node index
        for (let k = 0; k < curr.length; k++) {
          fullText += curr[k];
          charToNodeIndex.push(idx);
        }
      });

      // Find citation in the full text using head and tail segments
      const headLen = Math.min(100, citationText.length);
      const headSeg = citationText.substring(0, headLen);
      const tailLen = Math.min(100, citationText.length);
      const tailSeg = citationText.substring(Math.max(0, citationText.length - tailLen));

      const headMatch = findTextMatch(fullText, headSeg);
      const tailMatch = findTextMatch(fullText, tailSeg);

      let startNodeIndex = 0;
      let endNodeIndex = textNodes.length - 1;

      // Map start position to node index using charToNodeIndex
      if (headMatch) {
        let pos = Math.max(0, Math.min(headMatch.position, fullText.length - 1));
        let idx = charToNodeIndex[pos];

        // If on separator, scan forward then backward
        if (idx < 0) {
          for (let p = pos; p < charToNodeIndex.length; p++) {
            if (charToNodeIndex[p] >= 0) {
              idx = charToNodeIndex[p];
              break;
            }
          }
          if (idx < 0) {
            for (let p = pos; p >= 0; p--) {
              if (charToNodeIndex[p] >= 0) {
                idx = charToNodeIndex[p];
                break;
              }
            }
          }
        }

        if (idx >= 0) {
          startNodeIndex = idx;
        }
      }

      // Map end position to node index using charToNodeIndex
      if (tailMatch) {
        let endPos = tailMatch.position + tailMatch.length - 1;

        endPos = Math.max(0, Math.min(endPos, fullText.length - 1));
        let idx = charToNodeIndex[endPos];

        // If on separator, prefer backward scan (backward bias to avoid overshooting)
        if (idx < 0) {
          for (let p = endPos; p >= 0; p--) {
            if (charToNodeIndex[p] >= 0) {
              idx = charToNodeIndex[p];
              break;
            }
          }
          if (idx < 0) {
            for (let p = endPos; p < charToNodeIndex.length; p++) {
              if (charToNodeIndex[p] >= 0) {
                idx = charToNodeIndex[p];
                break;
              }
            }
          }
        }

        if (idx >= 0) {
          endNodeIndex = idx;
        }
      }

      // Highlight nodes from startNodeIndex to endNodeIndex
      // eslint-disable-next-line no-undef
      const overlappingNodes: Text[] = [];

      for (let i = startNodeIndex; i <= endNodeIndex; i++) {
        overlappingNodes.push(textNodes[i]);
      }

      // Highlight overlapping nodes and keep reference to first mark (lesson from DOCX)
      let firstMark: HTMLElement | undefined;

      overlappingNodes.forEach((node) => {
        const parent = node.parentElement;

        if (!parent) return;

        // Create highlight element
        const mark = document.createElement("mark");

        mark.className = "bg-warning/30 dark:bg-warning/40 px-1 rounded";
        // Remove hardcoded backgroundColor to let Tailwind handle dark mode
        mark.style.padding = "2px 4px";
        mark.style.borderRadius = "2px";

        // Replace text node with highlighted version
        const textContent = node.nodeValue || "";

        mark.textContent = textContent;
        parent.replaceChild(mark, node);

        // Keep reference to first mark for scrolling (lesson from DOCX)
        if (!firstMark) {
          firstMark = mark;
        }
      });

      // Scroll to first highlighted element with setTimeout (lesson from DOCX)
      if (firstMark) {
        setTimeout(() => {
          firstMark?.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "nearest",
          });
        }, 100);
      }
    } catch (err) {
      logger.error("Failed to highlight text in PPTX", err);
    }
  };

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      {/* Always render container so ref is available (lesson from DOCX) */}
      <div
        ref={containerRef}
        className="pptx-container max-w-4xl mx-auto"
        style={{
          /* PPTX preview styles */
          display: loading || error ? "none" : "block",
        }}
      />

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center h-[60vh]">
          <Spinner label="Loading PPTX..." />
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-danger-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default PPTXViewer;
